import OpenAI from 'openai';

const client = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

interface ImageAnalysis {
  type: 'coin' | 'note';
  name: string;
  description: string;
  condition?: string;
  authenticity?: string;
  historicalContext?: string;
  specifications?: string;
  investmentPotential?: string;
  hashtags?: string[];
  suggestedPrice?: number;
}

// Function to convert image to JPEG format
async function toJpeg(imageUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = imageUrl;
  });
}

// Function to check if image URL is a supported format
function isSupportedImageFormat(url: string): boolean {
  // blob: and data: URLs are handled separately — treat as supported
  if (url.startsWith('blob:') || url.startsWith('data:image/')) {
    return true;
  }
  const supportedFormats = ['png', 'jpeg', 'jpg', 'gif', 'webp'];
  const format = url.split('?')[0].split('.').pop()?.toLowerCase();
  return format ? supportedFormats.includes(format) : false;
}

export async function analyzeImage(imageUrl: string, userInput?: string): Promise<ImageAnalysis> {
  try {
    let processedImageUrl = imageUrl;

    // Convert image to JPEG if not in a supported format
    if (!isSupportedImageFormat(imageUrl)) {
      try {
        processedImageUrl = await toJpeg(imageUrl);
      } catch (conversionError) {
        console.error("Error converting image:", conversionError);
        throw new Error("Failed to process image. Please ensure it's a valid image file.");
      }
    }

    // Convert blob: URLs (from URL.createObjectURL) to base64 since OpenAI can't fetch them
    if (processedImageUrl.startsWith('blob:')) {
      try {
        const blob = await fetch(processedImageUrl).then(r => r.blob());
        processedImageUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (blobError) {
        console.error('Error converting blob URL:', blobError);
        throw new Error('Failed to process blob image URL.');
      }
    }

    const response = await client.chat.completions.create({
      model: "gpt-4o",
      messages: [{
        role: "user",
        content: [
          {
            type: "text",
            text: userInput
              ? `Analyze this numismatic item and write a compelling listing as if you're the seller speaking directly to collectors.

The item is titled: "${userInput}"

Use this title as context to create a rich, accurate description. If the title suggests a specific coin or note, research and describe it accordingly.

CRITICAL: You must format your response exactly as shown below, with no markdown symbols or formatting:

Type: [write exactly "coin" or "note" - no other text allowed]

Name: [use the provided title or an improved version if needed]

Description:
[Write a detailed description that matches the titled item. Be specific about year, denomination, historical period, ruler/issuer, mint marks, and any notable features.]

Condition:
[Assess and describe the visible condition]

Authentication:
[authenticity text]

Historical Context:
[historical text relevant to the titled item]

Specifications:
[technical specs matching the titled item]

Investment Potential:
[investment text]

Price Suggestion:
[suggested price in INR]

Hashtags:
[relevant hashtags]

IMPORTANT:
- Do not use any markdown symbols (*, **, #, etc.)
- Do not include brackets in your response
- The Type field must be exactly "coin" or "note" with no other text
- Keep each section on its own line
- Use plain text only
- Provide realistic market prices in INR`
              : `Analyze this numismatic item and write a compelling listing as if you're the seller speaking directly to collectors.

CRITICAL: You must format your response exactly as shown below, with no markdown symbols or formatting:

Type: [write exactly "coin" or "note" - no other text allowed]

Name: [title]

Description:
[description text]

Condition:
[condition text]

Authentication:
[authenticity text]

Historical Context:
[historical text]

Specifications:
[specifications text]

Investment Potential:
[investment text]

Price Suggestion:
[suggested price in INR]

Hashtags:
[hashtags]

IMPORTANT:
- Do not use any markdown symbols (*, **, #, etc.)
- Do not include brackets in your response
- The Type field must be exactly "coin" or "note" with no other text
- Keep each section on its own line
- Use plain text only
- Provide realistic market prices in INR`
          },
          {
            type: "image_url",
            image_url: {
              url: processedImageUrl
            }
          }
        ]
      }],
      max_tokens: 1500
    });

    if (!response.choices[0]?.message?.content) {
      throw new Error('No analysis received from API');
    }

    const analysis = response.choices[0].message.content;
    
    // Clean up the response by removing any markdown formatting
    const cleanContent = analysis
      .replace(/[*#`]/g, '') // Remove markdown symbols
      .replace(/\[|\]/g, ''); // Remove brackets

    // Enhanced section extraction with better regex patterns
    const extractSection = (sectionName: string, content: string): string | undefined => {
      const pattern = new RegExp(`${sectionName}:\\s*([^]*?)(?=\\n\\s*[A-Z][A-Za-z\\s]*:|$)`, 'i');
      const match = content.match(pattern);
      return match?.[1]?.trim();
    };

    // Extract and validate type with more robust handling
    const typeSection = extractSection('Type', cleanContent);
    if (!typeSection) {
      throw new Error('Type field is missing from the analysis');
    }

    const extractedType = typeSection.toLowerCase().trim();
    if (extractedType !== 'coin' && extractedType !== 'note') {
      throw new Error(`Invalid type "${extractedType}". Must be exactly "coin" or "note"`);
    }
    const type = extractedType as 'coin' | 'note';

    const name = extractSection('Name', cleanContent) || 'Unknown Collectible';
    const description = extractSection('Description', cleanContent) || '';
    const condition = extractSection('Condition', cleanContent);
    const authenticity = extractSection('Authentication', cleanContent);
    const historicalContext = extractSection('Historical Context', cleanContent);
    const specifications = extractSection('Specifications', cleanContent);
    const investmentPotential = extractSection('Investment Potential', cleanContent);
    const priceSuggestion = extractSection('Price Suggestion', cleanContent);

    // Extract suggested price
    const suggestedPrice = priceSuggestion ? 
      parseInt(priceSuggestion.replace(/[^\d]/g, '')) : 
      undefined;

    // Extract hashtags with improved handling
    const hashtagsSection = extractSection('Hashtags', cleanContent) || '';
    const hashtags = hashtagsSection
      .split(/[\s,]+/)
      .map(tag => tag.replace(/^#/, '').trim())
      .filter(tag => tag.length > 0);

    // Create a rich, formatted description that combines all sections
    const fullDescription = [
      description,
      condition && `\n\n✨ Condition\n${condition}`,
      authenticity && `\n\n🔒 Authentication Details\n${authenticity}`,
      historicalContext && `\n\n📜 Historical Significance\n${historicalContext}`,
      specifications && `\n\n📋 Technical Specifications\n${specifications}`,
      investmentPotential && `\n\n📈 Investment Value\n${investmentPotential}`,
      hashtags.length > 0 && `\n\n🏷️ Categories: ${hashtags.map(tag => '#' + tag).join(' ')}`
    ].filter(Boolean).join('');

    return {
      type,
      name,
      description: fullDescription,
      condition,
      authenticity,
      historicalContext,
      specifications,
      investmentPotential,
      hashtags,
      suggestedPrice
    };
  } catch (error) {
    console.error('Image analysis failed:', error);
    // Surface OpenAI API error details (status code, type, message)
    if (error && typeof error === 'object' && 'status' in error) {
      const apiErr = error as { status: number; message?: string; code?: string };
      throw new Error(`OpenAI API error ${apiErr.status}: ${apiErr.message || apiErr.code || 'unknown'}`);
    }
    throw new Error(
      error instanceof Error
        ? `Image analysis failed: ${error.message}`
        : 'Image analysis failed: Unknown error'
    );
  }
}